import { useGetFunctionalGroups, usePatchDocumentMutation } from "@hooks";
import { Document } from "@models";
import { Alert, TextField } from "@mui/material";
import {
  RegulatoryType,
  Template,
} from "@types";
import { useEffect, useState } from "react";
import {
  getFunctionalGroupLabelMap,
  getFunctionalGroupOptions,
} from "@utils";
import GenericModal from "src/components/GenericModal/GenericModal";
import GenericSearchSelect from "src/components/GenericSearchSelect";
import { MoreOptionsButton } from "./DocViewerMoreOptions";

export const useDocumentConfigAction = ({
  canEditConfig,
  deviceId,
  orgId,
  selectedDoc,
  templateConfig,
}: {
  canEditConfig: boolean;
  deviceId: string;
  orgId: string;
  selectedDoc: Document;
  templateConfig?: Template;
}) => {
  const patchDocumentMutation = usePatchDocumentMutation();
  const { data: functionalGroups } = useGetFunctionalGroups(orgId);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [configTitle, setConfigTitle] = useState("");
  const [initialConfigTitle, setInitialConfigTitle] = useState("");
  const [configFunctionalGroupId, setConfigFunctionalGroupId] =
    useState<string | null>(null);
  const [initialConfigFunctionalGroupId, setInitialConfigFunctionalGroupId] =
    useState<string | null>(null);
  const [configType, setConfigType] = useState<RegulatoryType | null>(null);
  const [initialConfigType, setInitialConfigType] =
    useState<RegulatoryType | null>(null);
  const [configError, setConfigError] = useState<string | null>(null);

  const trimmedConfigTitle = configTitle.trim();
  const hasConfigTitleChanged =
    trimmedConfigTitle !== initialConfigTitle.trim();
  const hasConfigFunctionalGroupUpdate =
    configFunctionalGroupId !== null &&
    configFunctionalGroupId !== initialConfigFunctionalGroupId;
  const hasConfigTypeUpdate =
    configType !== null && configType !== initialConfigType;
  const hasConfigChanges =
    hasConfigTitleChanged ||
    hasConfigFunctionalGroupUpdate ||
    hasConfigTypeUpdate;
  const isConfigSaveDisabled =
    patchDocumentMutation.isPending ||
    trimmedConfigTitle.length === 0 ||
    !hasConfigChanges;

  const functionalGroupOptions = getFunctionalGroupOptions(functionalGroups);
  const functionalGroupLabelMap = getFunctionalGroupLabelMap(functionalGroups);
  const resolvedTemplateGroupId = templateConfig?.functionalGroupAcronym
    ? ((functionalGroups || []).find(
        (fg) => fg.acronym === templateConfig.functionalGroupAcronym
      )?.id ?? null)
    : null;

  useEffect(() => {
    if (
      !isConfigModalOpen ||
      selectedDoc.functionalGroupId != null ||
      resolvedTemplateGroupId == null
    ) {
      return;
    }

    setConfigFunctionalGroupId((current) => current ?? resolvedTemplateGroupId);
  }, [
    isConfigModalOpen,
    resolvedTemplateGroupId,
    selectedDoc.functionalGroupId,
  ]);

  const handleOpenConfigModal = () => {
    if (!templateConfig) {
      return;
    }

    const nextConfigTitle =
      selectedDoc.title ?? templateConfig.defaultTitle ?? "";
    const nextConfigFunctionalGroupId =
      selectedDoc.functionalGroupId ?? resolvedTemplateGroupId;
    const nextConfigType =
      selectedDoc.regulatoryType ?? templateConfig.regulatoryType ?? null;

    setConfigTitle(nextConfigTitle);
    setInitialConfigTitle(nextConfigTitle);
    setConfigFunctionalGroupId(nextConfigFunctionalGroupId);
    setInitialConfigFunctionalGroupId(nextConfigFunctionalGroupId);
    setConfigType(nextConfigType);
    setInitialConfigType(nextConfigType);
    setConfigError(null);
    setIsConfigModalOpen(true);
  };

  const handleCloseConfigModal = () => {
    setConfigError(null);
    setIsConfigModalOpen(false);
  };

  const handleSaveConfig = async () => {
    if (!templateConfig || isConfigSaveDisabled) {
      return;
    }

    const data: {
      title?: string;
      functionalGroupId?: string;
      regulatoryType?: RegulatoryType;
    } = {};

    if (hasConfigTitleChanged) {
      data.title = trimmedConfigTitle;
    }

    if (hasConfigFunctionalGroupUpdate && configFunctionalGroupId) {
      data.functionalGroupId = configFunctionalGroupId;
    }

    if (hasConfigTypeUpdate && configType) {
      data.regulatoryType = configType;
    }

    try {
      await patchDocumentMutation.mutateAsync({
        orgId,
        deviceId,
        docId: selectedDoc.id,
        data,
      });
      setConfigError(null);
      setIsConfigModalOpen(false);
    } catch {
      setConfigError(
        "Failed to update document configuration. Please try again."
      );
    }
  };

  const configButton: MoreOptionsButton | null = canEditConfig
    ? {
        title: "Edit metadata",
        onClick: handleOpenConfigModal,
        disabled: false,
      }
    : null;

  const configModal = (
    <GenericModal
      isOpen={isConfigModalOpen}
      title="Document Configuration"
      handleCancel={handleCloseConfigModal}
      handleSave={handleSaveConfig}
      isLoading={patchDocumentMutation.isPending}
      isDisabled={isConfigSaveDisabled}
      width="500px"
    >
      {configError && <Alert severity="error">{configError}</Alert>}
      <TextField
        label="Document Name"
        size="small"
        fullWidth
        value={configTitle}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setConfigTitle(event.target.value)
        }
      />
      <div className="flex flex-col gap-2 mt-4">
        <GenericSearchSelect
          value={configFunctionalGroupId}
          setValue={(value) =>
            setConfigFunctionalGroupId(value as string | null)
          }
          options={functionalGroupOptions}
          getOptionLabel={(option) =>
            functionalGroupLabelMap[option] || option
          }
          label="Functional Group"
          disablePortal={false}
        />
      </div>
    </GenericModal>
  );

  return {
    configButton,
    configModal,
  };
};
